export type FilterOptionItem = { id: string; name: string }

export const normalizeFilterItem = (it: unknown): FilterOptionItem | null => {
  if (it == null) return null
  if (typeof it === 'string' || typeof it === 'number') {
    return { id: String(it), name: String(it) }
  }

  const record = it as Record<string, unknown>
  const id = record.id !== undefined ? String(record.id) : undefined
  const name =
    record.name !== undefined
      ? String(record.name)
      : record.label !== undefined
        ? String(record.label)
        : id !== undefined
          ? id
          : JSON.stringify(it)
  return { id: id ?? name, name }
}

const buildNodeFilterOptions = (
  currentFilter: { options?: unknown },
  appliedFilterState: Record<string, unknown>,
): FilterOptionItem[] => {
  const selectedStatesRaw = appliedFilterState['state'] || []
  const selectedStates = Array.isArray(selectedStatesRaw)
    ? selectedStatesRaw.map(String)
    : [String(selectedStatesRaw)]
  const nodeOptions = (currentFilter.options ?? {}) as Record<string, unknown>
  const nodesMap = new Map<string, FilterOptionItem>()
  const pushNode = (n: unknown) => {
    const normalized = normalizeFilterItem(n)
    if (!normalized) return
    if (!nodesMap.has(normalized.id)) nodesMap.set(normalized.id, normalized)
  }

  if (Array.isArray(nodeOptions)) {
    nodeOptions.forEach(pushNode)
  } else if (nodeOptions && typeof nodeOptions === 'object') {
    if (!selectedStates || selectedStates.length === 0) {
      Object.values(nodeOptions).forEach((arr: unknown) => {
        if (Array.isArray(arr)) arr.forEach(pushNode)
      })
    } else {
      selectedStates.forEach(sid => {
        const bucket = nodeOptions[String(sid)]
        if (Array.isArray(bucket)) bucket.forEach(pushNode)
      })
    }

    if (
      (!selectedStates || selectedStates.length === 0) &&
      Array.isArray(nodeOptions['null'])
    ) {
      nodeOptions['null'].forEach(pushNode)
    }
  }

  return Array.from(nodesMap.values())
}

export const buildFilteredFilterOptions = (
  currentFilter: { options?: unknown },
  filterKey: string,
  appliedFilterState: Record<string, unknown>,
): FilterOptionItem[] => {
  if (!currentFilter) return []

  if (filterKey === 'node') {
    return buildNodeFilterOptions(currentFilter, appliedFilterState)
  }

  const opts = currentFilter.options
  if (!opts) return []

  if (Array.isArray(opts)) {
    return opts.map(normalizeFilterItem).filter((item): item is FilterOptionItem => Boolean(item))
  }

  if (typeof opts === 'object') {
    return Object.entries(opts as Record<string, unknown>)
      .map(([k, v]) => {
        if (typeof v === 'string' || typeof v === 'number') {
          return { id: k, name: String(v) }
        }
        return normalizeFilterItem(v)
      })
      .filter((item): item is FilterOptionItem => Boolean(item))
  }

  return []
}

const applyDateFilters = (
  structuredFilters: Record<string, unknown>,
  appliedFilterState: Record<string, unknown>,
) => {
  if (!appliedFilterState.date) return

  const dateSelection = appliedFilterState.date
  const today = new Date()

  if (dateSelection === 'custom') {
    if (appliedFilterState.fromDate) {
      structuredFilters.created_at_after = appliedFilterState.fromDate
    }
    if (appliedFilterState.toDate) {
      structuredFilters.created_at_before = appliedFilterState.toDate
    }
    return
  }

  let startDate = new Date()
  let endDate = new Date()

  switch (dateSelection) {
    case 'today':
      startDate = new Date(today)
      endDate = new Date(today)
      break
    case 'last7':
      startDate = new Date(today)
      startDate.setDate(today.getDate() - 7)
      endDate = new Date(today)
      break
    case 'last30':
      startDate = new Date(today)
      startDate.setDate(today.getDate() - 30)
      endDate = new Date(today)
      break
    case 'last365':
      startDate = new Date(today)
      startDate.setDate(today.getDate() - 365)
      endDate = new Date(today)
      break
    default:
      startDate = new Date(today)
      endDate = new Date(today)
  }

  structuredFilters.created_at_after = startDate.toISOString().split('T')[0]
  structuredFilters.created_at_before = endDate.toISOString().split('T')[0]
}

const applyStateFilters = (
  structuredFilters: Record<string, unknown>,
  appliedFilterState: Record<string, unknown>,
  filterList: Array<{ key?: unknown; options?: unknown }>,
) => {
  const stateValues = appliedFilterState.state
  if (!Array.isArray(stateValues) || stateValues.length === 0) return

  const stateFilter = filterList.find(f => String(f.key).toLowerCase() === 'state')
  const options = stateFilter?.options || []
  const normalizedStates = Array.isArray(options)
    ? options.map((s: Record<string, unknown>) => ({
        id: String(s.id ?? s.value ?? s),
        name: String(s.name ?? s.label ?? s),
      }))
    : []

  structuredFilters.state = stateValues
    .map((sid: string) => normalizedStates.find(s => String(s.id) === String(sid)))
    .filter(Boolean)
}

const applyNodeFilters = (
  structuredFilters: Record<string, unknown>,
  appliedFilterState: Record<string, unknown>,
  filterList: Array<{ key?: unknown; options?: unknown }>,
) => {
  const nodeValues = appliedFilterState.node
  if (!Array.isArray(nodeValues) || nodeValues.length === 0) return

  const nodeFilter = filterList.find(f => String(f.key).toLowerCase() === 'node')
  const nodeOptions = nodeFilter?.options || {}
  const allNodes: unknown[] = []

  if (Array.isArray(nodeOptions)) {
    allNodes.push(...nodeOptions)
  } else {
    Object.values(nodeOptions as Record<string, unknown>).forEach((arr: unknown) => {
      if (Array.isArray(arr)) allNodes.push(...arr)
    })
  }

  const normalizedNodes = allNodes.map((n: Record<string, unknown>) => ({
    id: String(n.id ?? n.value ?? n),
    name: String(n.name ?? n.label ?? n),
  }))

  structuredFilters.node = nodeValues
    .map((nid: string) => normalizedNodes.find(n => String(n.id) === String(nid)))
    .filter(Boolean)
}

export const structureFilterValues = (
  appliedFilterState: Record<string, unknown>,
  filterList: Array<{ key?: unknown; options?: unknown }>,
) => {
  const structuredFilters: Record<string, unknown> = {}

  applyDateFilters(structuredFilters, appliedFilterState)
  applyStateFilters(structuredFilters, appliedFilterState, filterList)
  applyNodeFilters(structuredFilters, appliedFilterState, filterList)

  if (Array.isArray(appliedFilterState.dc_category) && appliedFilterState.dc_category.length > 0) {
    structuredFilters.dc_category = appliedFilterState.dc_category
  }

  if (appliedFilterState.status) {
    structuredFilters.status = appliedFilterState.status
  }

  if (appliedFilterState.request_type) {
    structuredFilters.request_type = appliedFilterState.request_type
  }

  return structuredFilters
}
